import { AlertCircle } from 'lucide-react';
import { twMerge as cn } from 'tailwind-merge';
import { LoadingSpinner } from './LoadingIndicator';
import {
  getInfrastructureNotice,
  isProjectInfrastructureReady,
  normalizeProjectProvisionStatus,
  ProjectProvisionStatus,
} from '../../types/onboarding';

export interface InfrastructureStatusNoticeProps {
  status?: ProjectProvisionStatus | string | null;
  provisionError?: string | null;
  className?: string;
  compact?: boolean;
}

export function InfrastructureStatusNotice({
  status,
  provisionError,
  className,
  compact = false,
}: InfrastructureStatusNoticeProps) {
  const normalized = normalizeProjectProvisionStatus(status);
  if (!normalized || isProjectInfrastructureReady(normalized)) {
    return null;
  }

  const { title, description } = getInfrastructureNotice(
    normalized,
    provisionError
  );
  const isFailed = normalized === 'failed';
  const isInProgress = normalized === 'in_progress';

  return (
    <div
      role="status"
      className={cn(
        'flex gap-3 rounded-lg border px-3.5 py-3',
        isFailed
          ? 'border-red-200 bg-red-50 text-red-950'
          : 'border-slate-200 bg-slate-50 text-slate-800',
        compact ? 'text-xs' : 'text-sm',
        className
      )}
    >
      <span className="flex shrink-0 items-center self-start pt-0.5" aria-hidden>
        {isInProgress ? (
          <LoadingSpinner
            size={compact ? 'xs' : 'sm'}
            className="text-slate-500"
          />
        ) : (
          <AlertCircle
            className={cn(
              isFailed ? 'text-red-600' : 'text-slate-500',
              compact ? 'h-3.5 w-3.5' : 'h-4 w-4'
            )}
          />
        )}
      </span>
      <div className="min-w-0 space-y-0.5">
        <p className={cn('font-medium leading-snug', compact && 'text-xs')}>
          {title}
        </p>
        <p
          className={cn(
            'leading-relaxed text-slate-600',
            isFailed && 'text-red-800/90',
            compact ? 'text-[11px]' : 'text-xs'
          )}
        >
          {description}
        </p>
      </div>
    </div>
  );
}
